/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useQuery } from "@apollo/client";
import { Box, Skeleton } from "@mui/material";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CATERING_RESTAURANTS, getCuisines, activePromotionsForCustomer } from "../../apollo/server";
import { TIMES, ymd } from "../../components/Catering/EventPicker";
import PlannerBanner, { GUEST_DEFAULT, GUEST_MIN, tomorrowKey } from "../../components/Catering/PlannerBanner";
import CatererCard from "../../components/Catering/CatererCard";
import SuggestRestaurant from "../../components/Catering/SuggestRestaurant";
import CuisineRow from "../../components/RestaurantList/CuisineRow";
import { FilterChip } from "../../components/RestaurantList/listUi";
import DealBanners from "../../components/RestaurantDetailComponent/Storefront/DealBanners";
import { useSearch } from "../../context/Search";
import { openWindowsForDay } from "../../components/Checkout/orderTimeSlots";
import { restaurantDayOf, wallParts } from "../../utils/restaurantClock";
import FlashMessage from "../../components/FlashMessage";
import { Header, LoginHeader } from "../../components/Header";
import { ClearCart } from "../../components/Modals";
import { useLocationContext } from "../../context/Location";
import { useOrderMode, ORDER_MODES } from "../../context/OrderMode";
import UserContext from "../../context/User";
import Seo from "../../components/Seo/Seo";
import { dealShort } from "../../utils/dealText";
import { cityLine } from "../../utils/shortAddress";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, RADIUS, TYPE, VISUALLY_HIDDEN, topBarOffset } from "../../utils/designTokens";
import {
  CATERING_PLANNER_KEY,
  cateringDateFor,
  cateringSettingsOf,
  saveCateringPlan,
} from "../../utils/catering";

const CATERING_QUERY = gql`
  ${CATERING_RESTAURANTS}
`;
const CUISINES_QUERY = gql`
  ${getCuisines}
`;
const PROMOS_QUERY = gql`
  ${activePromotionsForCustomer}
`;

const norm = (s) => String(s || "").trim().toLowerCase();
const atOf = (plan) => new Date(`${plan.date}T${plan.time}:00`);

const SORTS = [
  { key: "recommended", label: "Recommended" },
  { key: "nearest", label: "Nearest" },
  { key: "notice", label: "Lowest notice" },
];
const NO_FILTERS = { deals: false, veg: false, near: false, favourites: false };

/**
 * The event, kept for this browsing session (utils/catering.js reads its
 * guests too): tomorrow at noon for 20 to start with, then whatever was last
 * chosen while its day is still ahead.
 */
function startingPlan() {
  const fallback = { date: tomorrowKey(), time: "12:00", guests: GUEST_DEFAULT };
  try {
    const saved = JSON.parse(sessionStorage.getItem(CATERING_PLANNER_KEY) || "null");
    if (!saved) return fallback;
    return {
      date: typeof saved.date === "string" && saved.date >= ymd(new Date()) ? saved.date : fallback.date,
      time: TIMES.some((t) => t.value === saved.time) ? saved.time : fallback.time,
      guests: Number.isInteger(saved.guests) && saved.guests >= GUEST_MIN ? saved.guests : fallback.guests,
    };
  } catch (e) {
    return fallback;
  }
}

const leadOf = (r) => cateringSettingsOf(r).leadTimeMinutes;
const milesOf = (r) => (r.distance != null ? r.distance * 0.621371 : Infinity);

function Notice({ title, body, action }) {
  return (
    <Box sx={{ border: `1.5px dashed ${C.borderStrong}`, borderRadius: "20px", px: 3, py: 6, textAlign: "center" }}>
      <Box sx={{ width: 56, height: 56, mx: "auto", mb: 2, borderRadius: "16px", bgcolor: C.redTint, color: C.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <RoomServiceRoundedIcon aria-hidden="true" sx={{ fontSize: 28 }} />
      </Box>
      <Box component="p" sx={{ m: 0, fontFamily: FONT_BODY, fontSize: 17, fontWeight: 700, color: C.ink }}>{title}</Box>
      {body && <Box component="p" sx={{ ...TYPE.body, m: 0, mt: 0.75, color: C.muted }}>{body}</Box>}
      {action}
    </Box>
  );
}

const darkButtonSx = {
  ...BUTTON_RESET,
  ...FOCUS_RING,
  mt: 2.5,
  height: 44,
  px: 2.5,
  borderRadius: RADIUS.pill,
  bgcolor: C.ink,
  color: "#FFFFFF",
  fontFamily: FONT_BODY,
  fontSize: 14,
  fontWeight: 700,
  "&:hover": { bgcolor: "#000000" },
};

/**
 * Catering (backend migration 131): restaurants that cater, inside the one
 * radius a super admin sets. The planner's event (day, time, guests,
 * delivery or pickup) filters the caterers and rides along to checkout.
 */
function Catering() {
  const navigate = useNavigate();
  const { location } = useLocationContext();
  const { isLoggedIn, profile, stashCart, restaurant: cartRestaurant } = useContext(UserContext);
  const { orderMode, setOrderMode } = useOrderMode();
  const pickup = orderMode === ORDER_MODES.PICKUP;
  const [message, setMessage] = useState({});
  const [clearModal, setClearModal] = useState(false);
  const [navigateData, setNavigateData] = useState({});

  const [plan, setPlan] = useState(startingPlan);
  useEffect(() => {
    try {
      sessionStorage.setItem(CATERING_PLANNER_KEY, JSON.stringify(plan));
    } catch (e) {
      // Not remembered for this session; nothing else depends on it.
    }
  }, [plan]);
  const planAt = useMemo(() => atOf(plan), [plan]);

  // The header's search on this page, cleared on the way in and out so the
  // Restaurants page doesn't inherit it.
  const { search, setSearch } = useSearch();
  useEffect(() => {
    setSearch("");
    return () => setSearch("");
  }, []);

  const [choiceKey, setChoiceKey] = useState(null);
  const [filters, setFilters] = useState(NO_FILTERS);
  const [sort, setSort] = useState("recommended");

  const hasLocation = Boolean(location?.latitude && location?.longitude);
  const { data, loading, error } = useQuery(CATERING_QUERY, {
    variables: { latitude: Number(location?.latitude), longitude: Number(location?.longitude) },
    skip: !hasLocation,
    fetchPolicy: "cache-and-network",
  });
  const { data: cuisineData } = useQuery(CUISINES_QUERY, { fetchPolicy: "cache-first" });
  // Same variables as the sidebar's Deals badge.
  const { data: promoData } = useQuery(PROMOS_QUERY, {
    variables: { latitude: location?.latitude || null, longitude: location?.longitude || null, pickup },
    fetchPolicy: "cache-first",
  });

  const restaurants = data?.cateringRestaurants?.restaurants ?? [];
  const cateringPromos = useMemo(() => (promoData?.activePromotionsForCustomer || []).filter((p) => p.isCatering), [promoData]);
  const dealBy = useMemo(() => {
    const m = new Map();
    cateringPromos.forEach((p) => p.restaurantId && !m.has(String(p.restaurantId)) && m.set(String(p.restaurantId), p));
    return m;
  }, [cateringPromos]);

  // Browse by type: the caterers' cuisines (with their pictures), then their
  // catering categories (with their icons).
  const choices = useMemo(() => {
    const pictures = new Map((cuisineData?.getCuisines ?? []).map((c) => [c.name, c.image]));
    const names = [...new Set(restaurants.flatMap((r) => r.cuisines ?? []).filter(Boolean))];
    const seen = new Set(names.map(norm));
    const categories = restaurants
      .flatMap((r) => r.categories ?? [])
      .filter((c) => c.isCatering && c.title && !seen.has(norm(c.title)) && seen.add(norm(c.title)));
    return [
      ...names.map((name) => ({ key: `cuisine:${name}`, kind: "cuisine", title: name, icon: pictures.get(name) || null })),
      ...categories.map((c) => ({ key: `category:${c.title}`, kind: "category", title: c.title, icon: c.icon || null })),
    ];
  }, [restaurants, cuisineData]);
  const choice = choices.find((c) => c.key === choiceKey) || null;
  const favourites = profile?.favourite || [];

  // Every filter but the event, so an empty page can say which one emptied it.
  const matching = useMemo(() => {
    const q = norm(search);
    return restaurants.filter((r) => {
      if (q && !norm(r.name).includes(q) && !(r.cuisines ?? []).some((c) => norm(c).includes(q)) && !(r.cateringItemNames ?? []).some((n) => norm(n).includes(q))) return false;
      if (choice?.kind === "cuisine" && !(r.cuisines ?? []).includes(choice.title)) return false;
      if (choice?.kind === "category" && !(r.categories ?? []).some((c) => c.isCatering && norm(c.title) === norm(choice.title))) return false;
      if (filters.deals && !(r.cateringDealCount > 0 || dealBy.has(String(r._id)))) return false;
      if (filters.veg && !(r.cateringDietaryTags ?? []).some((t) => t === "Vegetarian" || t === "Vegan")) return false;
      if (filters.near && !(milesOf(r) <= 2)) return false;
      if (filters.favourites && !favourites.includes(r._id)) return false;
      return true;
    });
  }, [restaurants, search, choice, filters, dealBy, favourites]);

  // Can this caterer take the event at all: the group size, delivery or
  // pickup, and its hours that day (a food truck's hours are its stops, so it
  // is let through). Too little notice still shows, marked on the card.
  const takesEvent = useCallback(
    (r) => {
      const max = r.catering?.maxGuests;
      if (max && plan.guests > max) return false;
      if (pickup) {
        if (r.catering && r.catering.allowPickup === false && r.catering.deliveryMode !== "PICKUP_ONLY") return false;
      } else if (r.cateringDeliveryAvailable === false) {
        return false;
      }
      if (!r.isMobile) {
        // The planned time on this restaurant's clock (utils/restaurantClock.js).
        const windows = openWindowsForDay(restaurantDayOf(planAt, r.timezone), r.openingTimes);
        const minutes = wallParts(planAt, r.timezone).minutes;
        if (windows && !windows.some((w) => minutes >= w.start && minutes <= w.end)) return false;
      }
      return true;
    },
    [planAt, plan.guests, pickup]
  );
  const noticeOk = (r) => cateringDateFor(planAt, leadOf(r)).getTime() === planAt.getTime();

  const shown = useMemo(() => {
    const list = matching.filter(takesEvent);
    const hasDeal = (r) => (r.cateringDealCount > 0 || dealBy.has(String(r._id)) ? 1 : 0);
    const by = {
      recommended: (a, b) => hasDeal(b) - hasDeal(a) || milesOf(a) - milesOf(b),
      nearest: (a, b) => milesOf(a) - milesOf(b),
      notice: (a, b) => leadOf(a) - leadOf(b) || milesOf(a) - milesOf(b),
    }[sort];
    return list.slice().sort(by);
  }, [matching, takesEvent, sort, dealBy]);

  const clearFilters = () => {
    setSearch("");
    setChoiceKey(null);
    setFilters(NO_FILTERS);
  };

  // Opening a caterer carries the event along: its catering checkout starts
  // on the first time it can do on or after the event's.
  const toggleClearCart = useCallback(() => setClearModal((prev) => !prev), []);
  const openRestaurant = useCallback(
    (r) => {
      saveCateringPlan(r._id, cateringDateFor(planAt, leadOf(r)), plan.guests);
      const state = { id: r._id, name: r.name, image: r.image, slug: r.slug, catering: true };
      // A cart from another restaurant is parked, not binned, before moving on.
      if (cartRestaurant && String(r._id) !== String(cartRestaurant)) {
        setNavigateData(state);
        toggleClearCart();
        return;
      }
      navigate(`/restaurant/${r.slug}`, { state });
    },
    [cartRestaurant, planAt, plan.guests]
  );
  const navigateClearCart = useCallback(async () => {
    await stashCart();
    navigate(`/restaurant/${navigateData.slug}`, { state: navigateData });
  }, [navigateData]);

  const showMessage = useCallback((messageObj) => setMessage(messageObj), []);
  const toggleSnackbar = useCallback(() => setMessage({}), []);

  const findCaterers = () => {
    const el = document.getElementById("caterers-title");
    if (!el) return;
    const bar = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--rmf-topbar-h")) || 64;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - bar - 16, behavior: "smooth" });
  };

  const town = cityLine(location?.deliveryAddress || location?.label || "").split(",")[0];
  const firstLoad = loading && !data;
  const listTitle = choice ? `${choice.title} catering` : "Caterers near you";

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: C.bg }}>
      <Seo
        title="Catering Near You — Order Ahead for Groups"
        description="Find local restaurants that cater near you on RushMyFood. Order trays and group meals ahead for delivery or pickup."
        path="/catering"
      />
      <FlashMessage open={Boolean(message.type)} severity={message.type} alertMessage={message.message} handleClose={toggleSnackbar} />
      {isLoggedIn ? <Header /> : <LoginHeader showIcon />}

      <Box
        sx={{
          boxSizing: "border-box",
          width: "100%",
          px: { xs: 2, sm: 3, md: 4 },
          pt: { xs: topBarOffset(20), md: topBarOffset(28) },
          pb: 6,
          display: "flex",
          flexDirection: "column",
          gap: "36px",
        }}
      >
        <PlannerBanner
          plan={plan}
          onChange={(patch) => setPlan((p) => ({ ...p, ...patch }))}
          pickup={pickup}
          onPickup={(on) => setOrderMode(on ? ORDER_MODES.PICKUP : ORDER_MODES.DELIVERY)}
          onFind={findCaterers}
        />

        {!hasLocation ? (
          <Notice
            title="Set your address to see catering near you"
            action={
              <Box component="button" type="button" onClick={() => navigate("/")} sx={darkButtonSx}>
                Choose address
              </Box>
            }
          />
        ) : (
          <>
            {choices.length > 0 && (
              <CuisineRow
                title="Browse by type"
                id="types-title"
                choices={choices}
                selectedKey={choiceKey}
                onSelect={setChoiceKey}
                showCatering={false}
              />
            )}

            {cateringPromos.length > 0 && (
              <DealBanners
                title="Catering deals"
                look="catering"
                promos={cateringPromos}
                seeAllTo="/deals"
                eyebrowFor={(p) => (p.restaurantName ? `Catering deal · ${p.restaurantName}` : "Catering deal")}
                onOpenPromo={(p) => {
                  const r = restaurants.find((x) => String(x._id) === String(p.restaurantId));
                  if (r) openRestaurant(r);
                  else navigate(`/restaurant/${p.restaurantSlug}`, { state: { catering: true } });
                }}
                sectionSx={{}}
              />
            )}

            <Box component="section" aria-labelledby="caterers-title">
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", columnGap: 3, rowGap: 1.5, mb: 2.5 }}>
                <Box component="h2" id="caterers-title" sx={{ ...TYPE.sectionTitle, m: 0, color: C.ink }}>
                  {listTitle}
                  {!firstLoad && (
                    <Box component="span" sx={{ ...TYPE.body, fontSize: 15, fontWeight: 500, color: C.muted, ml: 1, letterSpacing: 0 }}>
                      · {shown.length} {shown.length === 1 ? "caterer" : "caterers"}
                    </Box>
                  )}
                </Box>
                {restaurants.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                    <Box role="group" aria-label="Filter caterers" sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      <FilterChip pressed={filters.deals} onClick={() => setFilters((f) => ({ ...f, deals: !f.deals }))}>Has deals</FilterChip>
                      <FilterChip pressed={filters.veg} onClick={() => setFilters((f) => ({ ...f, veg: !f.veg }))}>Vegetarian options</FilterChip>
                      <FilterChip pressed={filters.near} onClick={() => setFilters((f) => ({ ...f, near: !f.near }))}>Within 2 mi</FilterChip>
                      {isLoggedIn && (
                        <FilterChip pressed={filters.favourites} onClick={() => setFilters((f) => ({ ...f, favourites: !f.favourites }))}>Favourites</FilterChip>
                      )}
                    </Box>
                    <Box sx={{ position: "relative" }}>
                      <Box component="label" htmlFor="caterers-sort" sx={VISUALLY_HIDDEN}>Sort caterers</Box>
                      <Box
                        component="select"
                        id="caterers-sort"
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        sx={{ ...FOCUS_RING, appearance: "none", height: 40, pl: 2, pr: 5, borderRadius: RADIUS.pill, border: `1px solid ${C.borderStrong}`, bgcolor: C.bg, color: C.ink, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                      >
                        {SORTS.map((s) => (
                          <option key={s.key} value={s.key}>{`Sort: ${s.label}`}</option>
                        ))}
                      </Box>
                      <Box component="span" aria-hidden="true" sx={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-60%) rotate(45deg)", width: 7, height: 7, borderRight: `2px solid ${C.muted}`, borderBottom: `2px solid ${C.muted}`, pointerEvents: "none" }} />
                    </Box>
                  </Box>
                )}
              </Box>

              {firstLoad ? (
                <Box aria-busy="true" sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))", gap: "24px 20px" }}>
                  {[0, 1, 2].map((i) => (
                    <Box key={i}>
                      <Skeleton variant="rectangular" sx={{ aspectRatio: "16 / 9", height: "auto", borderRadius: "20px" }} />
                      <Skeleton width="60%" height={30} />
                      <Skeleton width="85%" />
                    </Box>
                  ))}
                </Box>
              ) : error && !data ? (
                <Notice title="Unable to load data" />
              ) : restaurants.length === 0 ? (
                <Notice title="No catering near you yet" body="We're bringing more caterers to your area." />
              ) : shown.length === 0 && matching.length > 0 ? (
                <Notice title="No caterers can take this event" body="Try another date or time, fewer guests, or the other of delivery and pickup." />
              ) : shown.length === 0 ? (
                <Notice
                  title="No caterers match these filters"
                  body="Try another type or clear the filters."
                  action={
                    <Box component="button" type="button" onClick={clearFilters} sx={darkButtonSx}>
                      Clear filters
                    </Box>
                  }
                />
              ) : (
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))", gap: "24px 20px" }}>
                  {shown.map((r) => (
                    <Box component="li" key={r._id} sx={{ minWidth: 0, display: "flex" }}>
                      <CatererCard
                        r={r}
                        deal={dealBy.has(String(r._id)) ? dealShort(dealBy.get(String(r._id))) : r.cateringDealCount > 0 ? "Catering deal" : null}
                        guests={plan.guests}
                        fits={noticeOk(r)}
                        leadMinutes={leadOf(r)}
                        onOpen={openRestaurant}
                        onMessage={showMessage}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {!firstLoad && <SuggestRestaurant city={town} onMessage={showMessage} />}
          </>
        )}
      </Box>
      <ClearCart isVisible={clearModal} toggleModal={toggleClearCart} action={navigateClearCart} />
    </Box>
  );
}

export default Catering;
